/**
 * SoleadLab 2.0 - Panel Component
 *
 * Draggable, collapsible panel for editor sidebars
 * and floating UI elements.
 */

import { useState, useRef, useEffect, ReactNode, MouseEvent } from 'react';
import './Panel.css';

interface PanelProps {
  /** Panel title */
  title: string;
  /** Panel content */
  children: ReactNode;
  /** Initial collapsed state */
  defaultCollapsed?: boolean;
  /** Allow collapsing */
  collapsible?: boolean;
  /** Allow dragging (for floating panels) */
  draggable?: boolean;
  /** Initial position (for floating panels) */
  initialPosition?: { x: number; y: number };
  /**
   * When this prop changes, the panel smoothly animates to the new position.
   * Use to push the panel out of the way when a ticket opens.
   * Does nothing while the user is actively dragging.
   */
  syncPosition?: { x: number; y: number };
  /** Fixed width */
  width?: number | string;
  /** Maximum height before scrolling */
  maxHeight?: number | string;
  /** Additional CSS class */
  className?: string;
  /** Header actions (buttons, etc.) */
  headerActions?: ReactNode;
  /** Callback when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Callback on every position change during drag + on mouse-up */
  onPositionChange?: (position: { x: number; y: number }) => void;
}

/**
 * Panel component for sidebars and floating UI
 */
export default function Panel({
  title,
  children,
  defaultCollapsed = false,
  collapsible = true,
  draggable = false,
  initialPosition,
  syncPosition,
  width,
  maxHeight,
  className = '',
  headerActions,
  onCollapsedChange,
  onPositionChange,
}: PanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [position, setPosition] = useState(initialPosition || { x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ x: 0, y: 0, panelX: 0, panelY: 0 });

  // When syncPosition changes (and we're not dragging), snap to the new position with a CSS transition
  const prevSyncRef = useRef(syncPosition);
  useEffect(() => {
    if (!syncPosition || isDragging) return;
    if (syncPosition === prevSyncRef.current) return;
    prevSyncRef.current = syncPosition;
    setPosition(syncPosition);
  }, [syncPosition, isDragging]);

  // Toggle collapsed state
  const handleToggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onCollapsedChange?.(newState);
  };

  // Start dragging
  const handleDragStart = (e: MouseEvent<HTMLDivElement>) => {
    if (!draggable) return;

    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panelX: position.x,
      panelY: position.y,
    };
  };

  // Handle dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: globalThis.MouseEvent) => {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;

      const panelW = panelRef.current?.offsetWidth  ?? 220;
      const panelH = panelRef.current?.offsetHeight ?? 60;
      // Clamp to the parent container so the panel stays within the map frame
      const container = panelRef.current?.parentElement;
      const maxX = (container?.offsetWidth  ?? window.innerWidth)  - panelW;
      const maxY = (container?.offsetHeight ?? window.innerHeight) - panelH;
      const clamp  = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

      const newPos = {
        x: clamp(dragStartRef.current.panelX + deltaX, 0, maxX),
        y: clamp(dragStartRef.current.panelY + deltaY, 0, maxY),
      };
      setPosition(newPos);
      onPositionChange?.(newPos); // keep parent in sync on every move
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onPositionChange]);

  const panelStyle: React.CSSProperties = {
    width,
    maxHeight: isCollapsed ? undefined : maxHeight,
    ...(draggable
      ? {
          position: 'absolute',  // absolute within .floating-layers → constrained to map area
          left: position.x,
          top: position.y,
          pointerEvents: 'auto',
          // Smooth transition when syncPosition nudges the panel; instant during drag
          transition: isDragging
            ? 'none'
            : 'left 0.22s cubic-bezier(0.2, 0.8, 0.2, 1), top 0.22s cubic-bezier(0.2, 0.8, 0.2, 1)',
        }
      : {}),
  };

  return (
    <div
      ref={panelRef}
      className={`sl-panel ${isCollapsed ? 'sl-panel--collapsed' : ''} ${
        draggable ? 'sl-panel--draggable' : ''
      } ${isDragging ? 'sl-panel--dragging' : ''} ${className}`}
      style={panelStyle}
    >
      {/* Header */}
      <div
        className="sl-panel__header"
        onMouseDown={draggable ? handleDragStart : undefined}
      >
        <h3 className="sl-panel__title">{title}</h3>

        <div className="sl-panel__actions">
          {headerActions}

          {collapsible && (
            <button
              type="button"
              className="sl-panel__toggle"
              onClick={handleToggleCollapse}
              aria-label={isCollapsed ? 'Expand panel' : 'Collapse panel'}
              aria-expanded={!isCollapsed}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={`sl-panel__toggle-icon ${
                  isCollapsed ? 'sl-panel__toggle-icon--collapsed' : ''
                }`}
              >
                <path d="M4 6l4 4 4-4" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {!isCollapsed && <div className="sl-panel__content">{children}</div>}
    </div>
  );
}
